import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "221",
  "slug": "what-can-you-do-with-geography-degree",
  "title": "What Can You Do With a Geography Degree? Careers in 2026",
  "excerpt": "Geography graduates are working in climate consultancies, tech companies, humanitarian organisations, and urban planning authorities. Here is a realistic look at where a geography degree takes you in 2026.",
  "tldr": "Geography graduates in 2026 work as GIS analysts, urban and transport planners, climate-risk modellers, environmental consultants, cartographers, location data scientists, and intelligence analysts. Tech, climate, and government are the three biggest hiring sectors, with starting salaries from around £28k in the UK to over $70k for GIS roles in the US.",
  "date": "2026-03-07",
  "category": "Education",
  "image": "/blog-images/world.jpg",
  "readTime": "2 min",
  "content": [
    {
      "type": "paragraph",
      "text": "Geography courses can include physical geography, social research, cartography, and data analysis. The jobs you can pursue depend on the modules you take and the skills you demonstrate, so compare actual role requirements with your coursework."
    },
    {
      "type": "heading",
      "text": "The Technical Track: GIS and Spatial Data"
    },
    {
      "type": "paragraph",
      "text": "Geographic Information Systems specialists are in strong demand across a wide range of industries. Urban planning authorities, utilities companies, logistics firms, telecommunications networks, and emergency services all depend on spatial data analysis to make decisions. A geography graduate comfortable with QGIS, ArcGIS, and Python scripting can step into analyst or junior GIS developer roles directly."
    },
    {
      "type": "paragraph",
      "text": "Tech companies have also become major employers of spatial talent. Apple, Google, Meta, and Amazon all run large mapping and location intelligence operations. A geography graduate who can code is genuinely sought after in these environments."
    },
    {
      "type": "heading",
      "text": "Environmental and Climate Careers"
    },
    {
      "type": "paragraph",
      "text": "Climate change has created an entire new category of geography-relevant employment. Environmental consultancies assess development sites for ecological impact, flood risk, and contamination. Carbon accounting firms need analysts who understand land cover and land use change."
    },
    {
      "type": "list",
      "items": [
        "Environmental consultant — assessing land and water quality, ecological impact, and regulatory compliance.",
        "Climate risk analyst — modelling flood, drought, and extreme weather exposure for insurers and banks.",
        "Remote sensing analyst — interpreting satellite and aerial imagery for land cover mapping or disaster response.",
        "Sustainability officer — helping organisations measure and reduce their environmental footprint.",
        "Urban planner — working on housing, transport, and land use policy.",
        "Humanitarian GIS officer — supporting organisations like UNHCR with spatial analysis of crisis response."
      ]
    },
    {
      "type": "heading",
      "text": "Skills That Cross Every Sector"
    },
    {
      "type": "paragraph",
      "text": "Compare job descriptions with work you can show: a map with a documented method, a fieldwork report, or a reproducible analysis. A degree title alone does not establish competence in every geographic specialty."
    },
    {
      "type": "heading",
      "text": "Building Your Edge While You Study"
    },
    {
      "type": "paragraph",
      "text": "The graduates who get the most interesting jobs are usually those who practiced their skills beyond the lecture hall. Learning Python for spatial analysis, building a portfolio of QGIS projects, and sharpening your satellite image interpretation — even through something as accessible as EarthGuessr's daily challenges — builds the kind of visual literacy that remote sensing and environmental roles reward."
    }
  ],
  "imageAlt": "Earth regional satellite composite with shaded relief",
  "imageCaption": "NASA Earth Observatory / MODIS, August 2004. Regional composite with shaded relief and bathymetry; it shows landforms, not political boundaries or current conditions.",
  "sources": [
    {
      "title": "NASA: Blue Marble Next Generation (image source)",
      "url": "https://science.nasa.gov/earth/earth-observatory/blue-marble-next-generation/"
    }
  ]
};
